import { useState, useEffect, useCallback, useRef } from "react";
import { getInsightsAggregate } from "../api";
import { getImpersonationDomain } from "../../impersonation/storage";
import { readRequestCache, writeRequestCache } from "../utils/requestCache";
import type {
  CriteriaFilter,
  DateRangeFilter,
  GetInsightsAggregateRequest,
  InsightsAggregateResponse,
  ScoreFilter,
  TagFilter,
} from "../types";

interface UseChartAggregateOptions {
  filters: TagFilter[];
  criteriaFilter?: CriteriaFilter;
  dateRangeFilter?: DateRangeFilter;
  scoreFilter?: ScoreFilter;
  groupBy?: string; // group the series by a single tag key
  groupLimit?: number; // how many tag values to render; server default when unset
  histogram?: boolean; // request the per-bucket score histogram in the response
  belowThreshold?: number; // request below_count: scores under this line, per bucket
  skip?: boolean; // short-circuit the fetch (e.g. no group key selected)
}

interface UseChartAggregateResult {
  response: InsightsAggregateResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useChartAggregate(
  options: UseChartAggregateOptions,
): UseChartAggregateResult {
  const {
    filters,
    criteriaFilter,
    dateRangeFilter,
    scoreFilter,
    groupBy,
    groupLimit,
    histogram,
    belowThreshold,
    skip,
  } = options;

  const buildParams = useCallback((): GetInsightsAggregateRequest => {
    const params: GetInsightsAggregateRequest = {};
    if (dateRangeFilter?.start_date) params.start_date = dateRangeFilter.start_date;
    if (dateRangeFilter?.end_date) params.end_date = dateRangeFilter.end_date;
    if (scoreFilter?.min_score != null) params.min_score = scoreFilter.min_score;
    if (scoreFilter?.max_score != null) params.max_score = scoreFilter.max_score;
    if (criteriaFilter?.values?.length) params.criteria = criteriaFilter.values;
    if (filters.length > 0) {
      params.tag = filters.map((f) => `${f.key}:${f.values.join(",")}`);
    }
    // group_limit only means anything alongside group_by; sending it otherwise
    // would fork the cache key between two identical ungrouped requests.
    if (groupBy) params.group_by = groupBy;
    if (groupBy && groupLimit != null) params.group_limit = groupLimit;
    if (histogram) params.histogram = true;
    if (belowThreshold != null) params.below_threshold = belowThreshold;
    return params;
  }, [
    dateRangeFilter,
    scoreFilter,
    criteriaFilter,
    filters,
    groupBy,
    groupLimit,
    histogram,
    belowThreshold,
  ]);

  // Stale-while-revalidate cache key: the params signature scoped by the active
  // impersonation domain so "view as customer" scopes never share data. `skip`
  // -> no key (no fetch). Kept in a ref so the fetch callback reads the latest.
  const params = buildParams();
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const cacheKey = skip
    ? null
    : `aggregate:${getImpersonationDomain() || "self"}:${JSON.stringify(params)}`;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  // Seed from cache so toggling back to this page paints the last view instantly
  // instead of flashing a skeleton while it refetches.
  const [response, setResponse] = useState<InsightsAggregateResponse | null>(
    () =>
      cacheKey
        ? readRequestCache<InsightsAggregateResponse>(cacheKey) ?? null
        : null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic id so a superseded (or background) response can't clobber a newer
  // one when filters change mid-flight.
  const seqRef = useRef(0);

  const revalidate = useCallback(async () => {
    const key = cacheKeyRef.current;
    if (skip || !key) {
      setLoading(false);
      return;
    }
    const cached = readRequestCache<InsightsAggregateResponse>(key);
    const seq = ++seqRef.current;
    // Only spin when there's nothing cached to show; otherwise revalidate quietly.
    if (cached === undefined) setLoading(true);
    setError(null);
    try {
      const data = await getInsightsAggregate(paramsRef.current);
      if (seq !== seqRef.current) return;
      writeRequestCache(key, data);
      setResponse(data);
    } catch (err: unknown) {
      if (seq !== seqRef.current) return;
      const message =
        err instanceof Error
          ? err.message
          : "Failed to fetch insights aggregate";
      // Keep showing cached data on a background failure; only surface the error
      // when we had nothing to show.
      if (cached === undefined) setError(message);
      console.error("Failed to fetch insights aggregate:", err);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [skip]);

  // Hydrate from cache + revalidate whenever the params (or scope) change.
  useEffect(() => {
    if (!cacheKey) {
      setLoading(false);
      return;
    }
    const cached = readRequestCache<InsightsAggregateResponse>(cacheKey);
    if (cached !== undefined) setResponse(cached);
    revalidate();
  }, [cacheKey, revalidate]);

  return { response, loading, error, refetch: revalidate };
}
